import { findRenewedIds, isLost } from "./renewal";
// Portföy Sağlık Puanı — üretim, yenileme ve komisyon trendlerinden 0-100 puan.
import { calcCommission, effectiveRate } from "./finance";

const DAY = 24 * 60 * 60 * 1000;

function parseDate(s) {
  if (!s) return null;
  const d = new Date(String(s).slice(0, 10));
  return isNaN(d.getTime()) ? null : d;
}

function inRange(dateStr, from, to) {
  const d = parseDate(dateStr);
  return d && d >= from && d < to;
}

function personalFor(p, branches, settings) {
  const rate = effectiveRate(p, branches);
  return calcCommission(p.premium, rate, settings.netDivisor, settings.personalShare).personal;
}

// Normalize a ratio (current/previous) into 0..1 tilted around parity.
// ratio=1 → 0.6, ratio=1.25 → 0.85, ratio>=1.5 → 1, ratio<=0.5 → 0.
function trendScore(current, previous) {
  if (current <= 0 && previous <= 0) return 0.3; // hiç veri yok → nötr düşük
  if (previous <= 0) return current > 0 ? 0.8 : 0.3;
  const r = current / previous;
  if (r >= 1.5) return 1;
  if (r <= 0.5) return 0;
  // linear interpolation between (0.5→0) and (1.5→1)
  return Math.max(0, Math.min(1, (r - 0.5) / 1));
}

/**
 * Compute portfolio health.
 * Returns { score, delta, trend, breakdown: {production, renewal, commission}, stats }
 */
export function computeHealth(policies, followups, branches, settings) {
  const now = new Date();
  const D90 = new Date(now.getTime() - 90 * DAY);
  const D180 = new Date(now.getTime() - 180 * DAY);
  const D60ahead = new Date(now.getTime() + 60 * DAY);
  const D30past = new Date(now.getTime() - 30 * DAY);
  const M3 = new Date(now.getTime() - 90 * DAY);
  const M6 = new Date(now.getTime() - 180 * DAY);
  const S = settings || { netDivisor: 1.12, personalShare: 60 };

  // Boş portföy: onboarding modu
  if (!policies || policies.length === 0) {
    return {
      score: 0,
      delta: 0,
      trend: "flat",
      empty: true,
      breakdown: {
        production: { score: 0, max: 40, note: "İlk poliçenizi ekleyin" },
        renewal: { score: 0, max: 30, note: "Yaklaşan yenileme yok" },
        commission: { score: 0, max: 30, note: "Komisyon verisi yok" },
      },
      stats: {
        newPolicies90d: 0, upcomingRenewals: 0, followedRenewals: 0,
        missedRenewals: 0, commission3m: 0, commissionPrev3m: 0,
      },
    };
  }

  // --- Üretim (0-40): son 90 gün poliçe sayısı vs önceki 90 gün ---
  const created = policies.map((p) => ({ p, cd: parseDate(p.createdAt) || parseDate(p.start) }));
  const prodCurrent = created.filter(({ cd }) => cd && cd >= D90).length;
  const prodPrev = created.filter(({ cd }) => cd && cd >= D180 && cd < D90).length;
  const premCurrent = created
    .filter(({ cd }) => cd && cd >= D90)
    .reduce((s, { p }) => s + Number(p.premium || 0), 0);
  const premPrev = created
    .filter(({ cd }) => cd && cd >= D180 && cd < D90)
    .reduce((s, { p }) => s + Number(p.premium || 0), 0);
  // Blend: yarısı sayı trendi, yarısı prim trendi. Ayrıca mutlak eşik: hiç yeni poliçe yoksa ceza.
  const prodMix = 0.5 * trendScore(prodCurrent, prodPrev) + 0.5 * trendScore(premCurrent, premPrev);
  const prodAbs = prodCurrent >= 3 ? 1 : prodCurrent / 3; // 3+ poliçe = tam puan
  const productionScore = Math.round((0.6 * prodMix + 0.4 * prodAbs) * 40);

  // --- Yenileme (0-30): önümüzdeki 60 gündeki yenilemelerden kaçının takip notu var + kaçırılmış poliçeler ---
  const renewedIds = findRenewedIds(policies);
  const upcoming = policies.filter((p) => {
    const end = parseDate(p.end);
    return end && !renewedIds.has(p.id) && !isLost(p) && end >= now && end <= D60ahead;
  });
  const followedIds = new Set(followups.map((f) => f.policyId).filter(Boolean));
  const withFollowup = upcoming.filter((p) => followedIds.has(p.id)).length;
  const followRatio = upcoming.length ? withFollowup / upcoming.length : 1;
  // Kaçırılmış: son 30 gün içinde bitmiş ve halen aktif takibi olmayan
  const missed = policies.filter((p) => {
    const end = parseDate(p.end);
    return end && !renewedIds.has(p.id) && !isLost(p) && end < now && end >= D30past && !followedIds.has(p.id);
  }).length;
  // Ceza: her kaçırılmış -0.15, max 0.6
  const missedPenalty = Math.min(0.6, missed * 0.15);
  const renewalRaw = Math.max(0, followRatio - missedPenalty);
  // Yaklaşan yenileme yoksa hafif nötr puan
  const renewalScore = Math.round((upcoming.length === 0 && missed === 0 ? 0.75 : renewalRaw) * 30);

  // --- Komisyon (0-30): son 3 ay kişisel hak ediş vs önceki 3 ay ---
  const comm3 = policies
    .filter((p) => inRange(p.start, M3, now))
    .reduce((s, p) => s + personalFor(p, branches, S), 0);
  const commPrev = policies
    .filter((p) => inRange(p.start, M6, M3))
    .reduce((s, p) => s + personalFor(p, branches, S), 0);
  const commissionScore = Math.round(trendScore(comm3, commPrev) * 30);

  const score = Math.max(0, Math.min(100, productionScore + renewalScore + commissionScore));

  // --- Trend: 4 hafta önceki hipotetik puanla kıyaslama ---
  // Basit yaklaşım: son 30 gün üretim - önceki 30 gün üretim → ± delta puanı olarak sun.
  const D30 = new Date(now.getTime() - 30 * DAY);
  const D60 = new Date(now.getTime() - 60 * DAY);
  const p30 = created.filter(({ cd }) => cd && cd >= D30).length;
  const p60 = created.filter(({ cd }) => cd && cd >= D60 && cd < D30).length;
  let delta = 0;
  if (p30 > p60) delta = Math.min(12, (p30 - p60) * 3);
  else if (p30 < p60) delta = Math.max(-12, (p30 - p60) * 3);
  const trend = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return {
    score,
    delta,
    trend,
    breakdown: {
      production: {
        score: productionScore,
        max: 40,
        note:
          prodCurrent === 0
            ? "Son 90 günde yeni poliçe yok"
            : `Son 90 günde ${prodCurrent} poliçe · önceki dönem ${prodPrev}`,
      },
      renewal: {
        score: renewalScore,
        max: 30,
        note:
          upcoming.length === 0
            ? missed
              ? `${missed} poliçe yenilenmedi · takip et`
              : "Yaklaşan yenileme yok"
            : `${withFollowup}/${upcoming.length} yenileme takipte${missed ? ` · ${missed} kaçtı` : ""}`,
      },
      commission: {
        score: commissionScore,
        max: 30,
        note:
          comm3 === 0 && commPrev === 0
            ? "Komisyon verisi yok"
            : comm3 >= commPrev
            ? `Son 3 ay hak ediş yükselişte`
            : `Son 3 ay hak ediş önceki döneme göre düştü`,
      },
    },
    stats: {
      newPolicies90d: prodCurrent,
      upcomingRenewals: upcoming.length,
      followedRenewals: withFollowup,
      missedRenewals: missed,
      commission3m: comm3,
      commissionPrev3m: commPrev,
    },
  };
}

export function healthGrade(score) {
  if (score >= 80) return { label: "Mükemmel", tone: "excellent", color: "#1a5fe0" };
  if (score >= 65) return { label: "İyi", tone: "good", color: "#3b82f6" };
  if (score >= 45) return { label: "Orta", tone: "fair", color: "#d97706" };
  return { label: "Dikkat", tone: "risk", color: "#dc2626" };
}
